/**
 * SectionValidationPlugin - Renders validation badges for song sections
 *
 * Architecture:
 * - Traverses SectionBlockNodes and counts lines per section
 * - Validates using sectionValidator utility
 * - Renders validation badges via React portal (outside Lexical's DOM)
 * - Positions badges absolutely next to section type labels
 * - Respects user preference: preferences.feedback.section.showValidation
 *
 * Key design: Uses React portal to avoid Lexical DOM reconciliation conflicts.
 * Badges are positioned absolutely relative to the editor container.
 */

import { useEffect, useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useEditorPreferences } from '../../../hooks/useEditorPreferences'
import { $getRoot } from 'lexical'
import { $isSectionBlockNode } from '../nodes/SectionBlockNode'
import type { SectionType } from '../nodes/SectionBlockNode'
import { validateSection, DEFAULT_SECTION_RULES } from '../../../utils/sectionValidator'
import type { SectionValidationRule } from '../../../utils/sectionValidator'
import { detectRhymeScheme } from '../../../utils/rhymeDetector'
import { SectionValidationBadge } from '../components/SectionValidationBadge'
import { LineStabilityIndicator } from '../components/LineStabilityIndicator'
import type { ValidationResult } from '../../../utils/sectionValidator'
import type { SectionRhymeSchemes } from '../../../types/rhymeScheme'
import { DEFAULT_SECTION_SCHEMES } from '../../../types/rhymeScheme'

interface SectionValidationPluginProps {
  sectionSchemes?: SectionRhymeSchemes
}

interface SectionValidationData {
  sectionKey: string
  sectionType: SectionType
  lineCount: number
  validation: ValidationResult
  position: { top: number; left: number } | null
}

/**
 * SectionValidationPlugin
 *
 * Traverses editor state to find sections, validates them, and renders badges.
 * Uses React portal pattern to render badges outside Lexical's DOM management.
 */
export default function SectionValidationPlugin({ sectionSchemes }: SectionValidationPluginProps) {
  const [editor] = useLexicalComposerContext()
  const { preferences } = useEditorPreferences()
  const [sections, setSections] = useState<SectionValidationData[]>([])
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)

  const isValidationEnabled = preferences.feedback.section.showValidation
  const isStabilityEnabled = preferences.feedback.section.showLineStability

  // Find the portal target container (editor-inner has position: relative)
  useEffect(() => {
    const rootElement = editor.getRootElement()
    if (!rootElement) return

    // Look for the section-block wrapper to insert badges after section-badge
    // Since we can't insert inside Lexical DOM, find parent container for portal
    const editorInner = rootElement.closest('.editor-inner') as HTMLElement
    setPortalContainer(editorInner)
  }, [editor])

  // Calculate badge positions based on section badge DOM positions
  // Returns a promise since detectRhymeScheme is async
  const calculatePositions = useCallback(async (): Promise<SectionValidationData[]> => {
    const rootElement = editor.getRootElement()
    const container = portalContainer
    if (!rootElement || !container) return []

    const containerRect = container.getBoundingClientRect()

    // PHASE 1: Collect section data from Lexical state (sync)
    interface SectionCollectionData {
      sectionType: SectionType
      sectionKey: string
      lineCount: number
      linesForAnalysis: string[]
      position: { top: number; left: number } | null
    }

    const collectedSections: SectionCollectionData[] = []

    editor.getEditorState().read(() => {
      const root = $getRoot()
      const children = root.getChildren()

      for (const child of children) {
        if ($isSectionBlockNode(child)) {
          const sectionType = child.getSectionType()
          const sectionKey = child.getKey()

          // Count lines within section
          const textContent = child.getTextContent()
          const lines = textContent.split('\n').filter((line) => line.trim())
          const firstLine = lines[0]?.toLowerCase()
          const isFirstLineLabel =
            firstLine === sectionType ||
            firstLine === child.getLabel()?.toLowerCase()
          const lineCount = isFirstLineLabel ? lines.length - 1 : lines.length

          // Lines for rhyme analysis (excluding label line if present)
          const linesForAnalysis = isFirstLineLabel ? lines.slice(1) : lines

          // Get section element position
          const sectionElement = editor.getElementByKey(sectionKey)
          let position: { top: number; left: number } | null = null

          if (sectionElement) {
            const sectionBadge = sectionElement.querySelector('.section-badge')
            if (sectionBadge) {
              const badgeRect = sectionBadge.getBoundingClientRect()
              // Position validation badge to the right of section badge
              position = {
                top: badgeRect.top - containerRect.top + container.scrollTop,
                left: badgeRect.right - containerRect.left + 4, // 4px gap after section badge
              }
            }
          }

          collectedSections.push({
            sectionType,
            sectionKey,
            lineCount,
            linesForAnalysis,
            position,
          })
        }
      }
    })

    // PHASE 2: Process rhyme analysis asynchronously
    const sectionData: SectionValidationData[] = []

    for (const section of collectedSections) {
      // Derive actual rhyme pattern from section lines (async)
      const rhymeAnalysis = await detectRhymeScheme(section.linesForAnalysis)
      const actualRhymePattern = rhymeAnalysis.pattern

      // Get the expected pattern from user's scheme configuration
      const schemeConfig = sectionSchemes?.[section.sectionType] || DEFAULT_SECTION_SCHEMES[section.sectionType]
      let expectedPattern: string | undefined = undefined
      if (schemeConfig) {
        if (schemeConfig.pattern === 'FREE') {
          expectedPattern = undefined // FREE means no expected pattern
        } else if (schemeConfig.pattern === 'CUSTOM' && schemeConfig.customPattern) {
          expectedPattern = schemeConfig.customPattern
        } else if (schemeConfig.pattern !== 'CUSTOM') {
          expectedPattern = schemeConfig.pattern
        }
      }

      // Create validation rules with the expected pattern
      const validationRules: SectionValidationRule = {
        ...DEFAULT_SECTION_RULES[section.sectionType],
        expectedRhymePattern: expectedPattern
      }

      // Validate section with user's configured scheme
      const validation = validateSection(section.sectionType, section.lineCount, validationRules, actualRhymePattern)

      sectionData.push({
        sectionKey: section.sectionKey,
        sectionType: section.sectionType,
        lineCount: section.lineCount,
        validation,
        position: section.position,
      })
    }

    return sectionData
  }, [editor, portalContainer, sectionSchemes])

  // Update sections on editor changes (debounced)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    let isMounted = true

    const updateSections = () => {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        // calculatePositions is async now
        calculatePositions()
          .then((data) => {
            if (isMounted) setSections(data)
          })
          .catch((error) => {
            console.error('[SectionValidationPlugin] Error calculating positions:', error)
          })
      }, 50)
    }

    const unregister = editor.registerUpdateListener(() => {
      updateSections()
    })

    // Initial calculation
    updateSections()

    return () => {
      isMounted = false
      unregister()
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [editor, calculatePositions])

  // Recalculate on scroll/resize
  useEffect(() => {
    let isMounted = true

    const handleScrollOrResize = () => {
      calculatePositions()
        .then((data) => {
          if (isMounted) setSections(data)
        })
        .catch((error) => {
          console.error('[SectionValidationPlugin] Error on scroll/resize:', error)
        })
    }

    const editorInner = portalContainer
    editorInner?.addEventListener('scroll', handleScrollOrResize)
    window.addEventListener('resize', handleScrollOrResize)

    return () => {
      isMounted = false
      editorInner?.removeEventListener('scroll', handleScrollOrResize)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [portalContainer, calculatePositions])

  // Update when preference changes
  useEffect(() => {
    let isMounted = true

    calculatePositions()
      .then((data) => {
        if (isMounted) setSections(data)
      })
      .catch((error) => {
        console.error('[SectionValidationPlugin] Error on preference change:', error)
      })

    return () => {
      isMounted = false
    }
  }, [isValidationEnabled, isStabilityEnabled, calculatePositions])

  // Memoize visible sections
  const visibleSections = useMemo(() => {
    // Show overlay if either feature is enabled
    if (!isValidationEnabled && !isStabilityEnabled) return []
    return sections.filter((s) => s.position !== null)
  }, [sections, isValidationEnabled, isStabilityEnabled])

  // Don't render if no portal container or both features disabled
  if (!portalContainer || (!isValidationEnabled && !isStabilityEnabled) || visibleSections.length === 0) {
    return null
  }

  // Render badges as overlay using React portal
  const overlay = (
    <div
      className="section-validation-overlay"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 10,
      }}
    >
      {visibleSections.map((section) => (
        <div
          key={section.sectionKey}
          style={{
            position: 'absolute',
            top: section.position!.top,
            left: section.position!.left,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            pointerEvents: 'auto',
          }}
        >
          {isValidationEnabled && (
            <SectionValidationBadge validation={section.validation} />
          )}
          {isStabilityEnabled && (
            <LineStabilityIndicator lineCount={section.lineCount} />
          )}
        </div>
      ))}
    </div>
  )

  return createPortal(overlay, portalContainer)
}
